import { useState } from 'react';
import { NJButton, NJCheckbox, NJHeading, NJText } from '@engie-group/fluid-design-system-react';
import DataTable from '@shared/DataTable';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { fmt, planStatus } from './domain';
import { energyLabel } from './data';
import { DecisionNote } from './DecisionPanel';
import type { DevelopmentPlan, Scenario } from './types';

interface Row { id: string; plan: string; scenario: string; energy: string; resultEnergy: string; status: string; date: string; count: string; demand: string; length: string; phasing: string }
export function Comparison({ plans, scenarios, open, onDecision }: {
  plans: DevelopmentPlan[]; scenarios: Scenario[]; open: (id: string) => void; onDecision: (id: string) => void;
}) {
  const [selected, setSelected] = useState<string[]>(['p1', 'p2']);
  const rows: Row[] = plans.filter(p => selected.includes(p.id)).map(p => ({
    id: p.id, plan: p.name, scenario: scenarios.find(s => s.id === p.scenarioId)?.name ?? 'Parent indisponible',
    energy: energyLabel(p.energy), resultEnergy: p.result ? energyLabel(p.result.energy ?? p.energy) : '—', status: planStatus(p),
    date: p.result ? `${new Date(p.result.calculatedAt).toLocaleString('fr-FR')} · données v${p.result.revision}` : 'Non calculé',
    count: p.result ? String(p.result.subscribers) : 'Non calculé',
    demand: p.result ? fmt(p.result.demand) : 'Non calculée',
    length: p.result ? fmt(p.result.lengthKm) : 'Non calculé',
    phasing: `${p.pipes.filter(pipe => !!pipe.month).length} / ${p.pipes.length} canalisations`,
  }));
  const header: ColDef<Row>[] = [
    { field: 'plan', headerName: 'Plan', pinned: 'left', minWidth: 200 },
    { field: 'scenario', headerName: 'Scénario' }, { field: 'energy', headerName: 'Énergie actuelle' },
    { field: 'resultEnergy', headerName: 'Énergie au calcul' },
    { field: 'status', headerName: 'État actuel' }, { field: 'date', headerName: 'Dernier résultat', minWidth: 260 },
    { field: 'count', headerName: 'Abonnés au calcul' }, { field: 'demand', headerName: 'Demande au calcul · MWh/an', minWidth: 230 },
    { field: 'length', headerName: 'Linéaire calculé · km' }, { field: 'phasing', headerName: 'Phasage actuel' },
    { headerName: 'Consulter', cellRenderer: (p: ICellRendererParams<Row>) => p.data ?
      <NJButton label="Ouvrir le plan" scale="sm" emphasis="minimal" onClick={() => { if (p.data) open(p.data.id); }} /> : null },
  ];
  return <section className="pu-review sp-stack">
    <NJHeading as="h2" scale="lg">Comparaison de plans</NJHeading>
    <NJText>Aucun classement. Les consommations et linéaires sont ceux du dernier calcul, pas des valeurs courantes.
      Le phasage est l’état de saisie actuel. Des énergies et versions différentes ne sont pas équivalentes.</NJText>
    <DecisionNote id="comparison" onOpen={onDecision} />
    <NJText scale="sm">Indicateurs illustratifs. Linéaire de démonstration : somme des géométries calculées ; périmètre métier à confirmer.</NJText>
    <div className="sp-row">{plans.map(p => <NJCheckbox key={p.id} label={`${p.name} · ${scenarios.find(s => s.id === p.scenarioId)?.name}`}
      isChecked={selected.includes(p.id)} onChange={(_, checked) => setSelected(ids => checked ? [...ids, p.id] : ids.filter(id => id !== p.id))} />)}</div>
    {rows.length < 2 ? <NJText role="status">Sélectionnez au moins deux plans pour comparer.</NJText> :
      <div className="pu-comparison-grid"><DataTable data={rows} header={header} testId="pu-comparison" /></div>}
    <NJText scale="sm">Aucune limite métier de sélection décidée. Un résultat « À recalculer » reste historique et consultable.</NJText>
  </section>;
}
